<template>
  <footer class="kds-footer">
    <div class="kds-footer__inner">
      <div class="kds-footer__columns">
        <section
          v-for="col in columns"
          :key="col.title"
          class="kds-footer__col"
        >
          <KdsEyebrow class="kds-footer__col-title">{{ col.title }}</KdsEyebrow>
          <ul class="kds-footer__col-links">
            <li v-for="link in col.links" :key="link.label">
              <router-link v-if="link.to" class="kds-footer__link" :to="link.to">{{ link.label }}</router-link>
              <button v-else-if="link.action" class="kds-footer__link kds-footer__link--btn" @click="link.action()">{{ link.label }}</button>
              <a v-else class="kds-footer__link" :href="link.href">{{ link.label }}</a>
            </li>
          </ul>
        </section>
      </div>
      <div class="kds-footer__bottom">
        <span class="kds-footer__brand">{{ brand }}</span>
        <div
          v-if="statusTo"
          class="kds-footer__status-wrap"
          @mouseenter="onStatusEnter"
          @mouseleave="onStatusLeave"
        >
          <router-link
            :to="statusTo"
            class="kds-footer__status-link"
            :aria-label="`System status: ${statusLabel}. View status page.`"
            aria-describedby="footer-status-tooltip"
          >
            <KdsChip tone="persona" segment="moss" class="kds-footer__status">
              <span class="kds-footer__status-label">{{ statusLabel }}</span>
            </KdsChip>
          </router-link>
          <KdsFooterStatusTooltip
            :visible="hovered"
            :services="services"
            :loading="loading"
          />
        </div>
        <KdsChip v-else tone="persona" segment="moss" class="kds-footer__status">
          <span class="kds-footer__status-label">{{ statusLabel }}</span>
        </KdsChip>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import KdsChip from '@/kds/primitives/KdsChip.vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import KdsFooterStatusTooltip from '@/kds/components/KdsFooterStatusTooltip.vue';
import { useStatusTooltip } from '@/composables/useStatusTooltip';

interface FooterLink {
  label: string;
  href?: string;
  to?: string | RouteLocationRaw;
  action?: () => void;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

interface Props {
  brand: string;
  statusLabel: string;
  columns: FooterColumn[];
  statusTo?: RouteLocationRaw;
}

defineProps<Props>();

const { services, loading, ensureLoaded } = useStatusTooltip();
const hovered = ref(false);

const onStatusEnter = (): void => {
  hovered.value = true;
  ensureLoaded().catch(console.error);
};

const onStatusLeave = (): void => {
  hovered.value = false;
};
</script>

<style scoped lang="scss">
.kds-footer {
  background: var(--paper);
  border-top: var(--border);
  padding: var(--space-9) var(--space-7);

  &__inner {
    max-width: 1280px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
  }

  &__columns {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: var(--space-6);

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (min-width: 960px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  &__col {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__col-links {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__link {
    color: var(--stone);
    text-decoration: none;
    font-size: var(--fs-body-sm);
    transition: color var(--d-fast) var(--ease-out);

    &:hover {
      color: var(--ink);
    }

    &--btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: var(--font-ui);
      text-align: left;

      &:focus-visible {
        outline: none;
        box-shadow: var(--shadow-focus);
        border-radius: var(--r-2);
      }
    }
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--space-3);
    padding-top: var(--space-5);
    border-top: var(--border);
  }

  &__brand {
    font-size: var(--fs-body);
    font-weight: var(--fw-semibold);
    color: var(--ink);
  }

  &__status-wrap {
    position: relative;
  }

  &__status-link {
    text-decoration: none;
    border-radius: var(--r-pill);
    transition: box-shadow var(--d-fast) var(--ease-out);

    &:hover .kds-footer__status {
      border-color: var(--mist-2);
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }
}
</style>
